import { createSelector } from 'reselect'
import { TRAVEL_PROTECTION_VALID_COUNTRIES } from 'constants/config/region'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import { selectNewInsuranceUiIsVariant } from 'lib/insurance/newInsuranceUiExperiment'
import { isSpoofed } from 'selectors/isSpoofed'
import { getFunnelFeatures } from '../funnel'
import { isCruiseCheckout } from '../view/cruise'
import { isStandaloneFlights } from '../view/flights'
import { checkoutHasPreselectedUpstreamInsurance } from '../view/insurance'
import isPassportCardInsuranceSupportedForToursCruises from './isPassportCardInsuranceSupportedForToursCruises'
import isPassportCardInsuranceSupportedViaCampaign from './isPassportCardInsuranceSupportedViaCampaign'

/**
 * Whether the checkout is one where cancellation protection and travel protection can be offered
 * side by side, judged on the session and cart alone: an AU or NZ customer, not spoofed, not a
 * post-purchase flow, not a standalone-flights or cruise cart, no protection already chosen
 * upstream, both products switched on by the funnel optimiser and no PassportCard takeover.
 *
 * Product availability is deliberately not part of this decision so that it can feed the
 * cancellation protection destination rules without a circular dependency.
 */
export const isSideBySideInsuranceCheckout = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => isSpoofed(state),
  (state: App.State) => !!findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => isStandaloneFlights(state),
  (state: App.State) => isCruiseCheckout(state),
  (state: App.State) => checkoutHasPreselectedUpstreamInsurance(state),
  (state: App.State) => getFunnelFeatures(state),
  (state: App.State) => isPassportCardInsuranceSupportedViaCampaign(state),
  (state: App.State) => isPassportCardInsuranceSupportedForToursCruises(state),
  (
    currentRegionCode,
    isSpoofed,
    isPostPurchase,
    isStandaloneFlights,
    isCruise,
    hasPreselectedUpstreamInsurance,
    funnelFeatures,
    isPassportCardViaCampaign,
    isPassportCardForToursCruises,
  ): boolean => {
    // The travel protection products shown next to CFMR only exist for AU and NZ customers. Other
    // regions with XCover insurance (e.g. SG) keep their existing sections, so CFMR stays offered
    // there unchanged.
    if (!TRAVEL_PROTECTION_VALID_COUNTRIES.includes(currentRegionCode)) return false
    if (isSpoofed || isPostPurchase || isStandaloneFlights || isCruise) return false
    if (hasPreselectedUpstreamInsurance) return false
    if (!funnelFeatures.cfmr || !funnelFeatures.travelProtection) return false
    if (isPassportCardViaCampaign || isPassportCardForToursCruises) return false

    return true
  },
)

/**
 * Maldives and Fiji stays normally keep travel insurance as their only protection. Customers in
 * the treatment cohort of the side-by-side insurance experiment see cancellation protection
 * there too, so the destination rule is lifted for them wherever cancellation protection
 * availability is decided.
 */
export const shouldOfferBookingProtectionForFijiAndMaldives = createSelector(
  (state: App.State) => isSideBySideInsuranceCheckout(state),
  (state: App.State) => selectNewInsuranceUiIsVariant(state),
  (isSideBySideCheckout, isVariant): boolean => isSideBySideCheckout && isVariant,
)
